/**
 * useWarehouses — loads warehouse rows from `GET /api/warehouse/warehouses`.
 *
 * Warehouses are a growing, DB-driven list (`warehouse.warehouse`), not a
 * fixed two/three-value enum — new own-3PL sites (e.g. Brixton SE24) or
 * distributor/channel warehouses can be added without a frontend change.
 * Any picker that lists warehouses should source its options from here
 * instead of a hardcoded array.
 *
 * A tiny module-level cache avoids refetching the same query on every
 * mount (the list changes rarely); it is keyed by the resolved query string
 * and shared across all hook instances for the lifetime of the page.
 */
import { useEffect, useState } from 'react';

import { useAuth } from '../AuthContext';
import { useApi } from '../api';

export interface WarehouseInfo {
  code: string;
  name: string;
  warehouse_type: string;
  region_code: string;
  currency_code: string;
}

export interface UseWarehousesOptions {
  /** Filters to a single `warehouse_type` (e.g. `'own_3pl'`). */
  warehouseType?: string;
  /** Client-side filter to a single `region_code` (e.g. `'GB'`). */
  regionCode?: string;
  /** When false, includes inactive warehouses too (default: active only). */
  activeOnly?: boolean;
}

export interface UseWarehousesResult {
  warehouses: WarehouseInfo[];
  loading: boolean;
  error: string | null;
}

const cache = new Map<string, WarehouseInfo[]>();
const inFlight = new Map<string, Promise<WarehouseInfo[]>>();

function cacheKey(warehouseType: string | undefined, activeOnly: boolean): string {
  return `${warehouseType || ''}::${activeOnly ? 'active' : 'all'}`;
}

/**
 * Loads warehouses matching the given filters. `regionCode` is applied
 * client-side (the endpoint does not take it as a query param); `warehouseType`
 * and `activeOnly` are sent as query params and used as the cache key.
 */
export function useWarehouses(options: UseWarehousesOptions = {}): UseWarehousesResult {
  const { warehouseType, regionCode, activeOnly = true } = options;
  const { token, logout } = useAuth();
  const { apiFetch } = useApi(token, logout);

  const [warehouses, setWarehouses] = useState<WarehouseInfo[]>(
    () => cache.get(cacheKey(warehouseType, activeOnly)) || [],
  );
  const [loading, setLoading] = useState<boolean>(
    () => !cache.has(cacheKey(warehouseType, activeOnly)),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const key = cacheKey(warehouseType, activeOnly);
    const cached = cache.get(key);
    if (cached) {
      setWarehouses(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchPromise =
      inFlight.get(key) ||
      (async () => {
        const params = new URLSearchParams();
        params.set('active_only', String(activeOnly));
        if (warehouseType) params.set('warehouse_type', warehouseType);
        const res = await apiFetch(`/api/warehouse/warehouses?${params.toString()}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.detail || 'Failed to load warehouses');
        }
        const body = (await res.json()) as WarehouseInfo[];
        const rows = Array.isArray(body) ? body : [];
        cache.set(key, rows);
        return rows;
      })();

    inFlight.set(key, fetchPromise);

    fetchPromise
      .then((rows) => {
        if (!cancelled) {
          setWarehouses(rows);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError((err as Error)?.message || 'Failed to load warehouses');
          setLoading(false);
        }
      })
      .finally(() => {
        inFlight.delete(key);
      });

    return () => {
      cancelled = true;
    };
  }, [apiFetch, warehouseType, activeOnly]);

  const filtered = regionCode
    ? warehouses.filter((w) => w.region_code === regionCode)
    : warehouses;

  return { warehouses: filtered, loading, error };
}

export default useWarehouses;
